/**
 * Game audio: one music bed that cross-fades between day, night and the arena,
 * plus fire-and-forget sound effects.
 *
 * Design notes worth keeping:
 *
 * - **Autoplay.** Browsers refuse audio until the player has interacted with the
 *   page, and a rejected `play()` is an unhandled rejection. Everything here is
 *   therefore lazy: nothing is fetched or played until the first real gesture, and
 *   every `play()` swallows its rejection.
 * - **No preloading the world.** SFX are fetched on first use and cached, so a
 *   player who never fights never downloads the combat set. The music beds are the
 *   only large files and only the currently-playing one is ever loaded.
 * - **Cheap repeats.** A sound that can fire several times a second (footsteps,
 *   chopping) needs overlapping playback, so each cue keeps a small pool of
 *   elements and round-robins them rather than restarting one.
 * - **But not TOO cheap.** Ten orcs swinging at once must not stack ten copies of
 *   one file. Every cue passes a gate first (`limiter.ts`): a short retrigger
 *   cooldown plus a hard cap on simultaneous copies of the SAME cue. World sounds
 *   additionally fade with distance from the hero.
 * - **The view owns the music, not the other way round.** Views declare where the
 *   player is (`setAudioView` / `setAudioOverlay` / `setWorldPhase`) and
 *   `resolveTrack` decides what plays. See `track.ts`.
 * - **Muted by default is wrong, loud by default is worse.** Music sits well under
 *   the effects, and both persist through the settings store.
 */

import { createCueGate, distanceGain, pickVariant } from './limiter';
import { resolveTrack, TRACK_GAIN, type AudioScene, type AudioView, type MusicTrack } from './track';

export type { MusicTrack, AudioView } from './track';

const MUSIC_DIR = '/audio/music';
const SFX_DIR = '/audio/sfx';

/** Every sound effect the game can fire. */
export type Sfx =
  | 'axe_chop' | 'tree_fall' | 'pickaxe_hit' | 'rock_break'
  | 'hoe_till' | 'water_pour' | 'plant_seed' | 'harvest_crop' | 'gather_plant'
  | 'sword_swing' | 'bow_shot' | 'arrow_hit'
  | 'player_hurt' | 'player_death' | 'mob_aggro' | 'mob_death' | 'arena_death'
  | 'footstep_grass' | 'footstep_soil' | 'horse_mount' | 'horse_gallop'
  | 'build_place' | 'craft_complete' | 'pickup_item'
  | 'coin_purchase' | 'level_up' | 'quest_complete'
  | 'ui_click' | 'ui_open' | 'ui_close' | 'chat_message'
  | 'campfire';

/** How many overlapping copies a cue may play at once. */
const POOL = 3;
/**
 * Cap and cooldown for ONE cue. Three copies is the point where a crowd still
 * reads as a crowd; the fourth is mud. 80ms is under the ~100ms two clicks need
 * to be heard as separate events, so a genuine double-hit still sounds twice.
 */
const MAX_CONCURRENT = 3;
const RETRIGGER_MS = 80;

/** Per-cue overrides where the shared default is wrong. */
const CUE_COOLDOWN_MS: Partial<Record<Sfx, number>> = {
  // A held chop / mine is a rhythm, driven by the caller's own 300ms cadence —
  // it must never be swallowed by the shared gate.
  axe_chop: 200,
  pickaxe_hit: 200,
  footstep_grass: 220,
  footstep_soil: 220,
  horse_gallop: 400,
  // Ambience: one crackle at a time, well spaced.
  campfire: 1_500,
  // Deaths and level-ups are events, not textures — never let two overlap.
  player_death: 1_000,
  level_up: 1_000,
  quest_complete: 800,
};
const CUE_MAX_CONCURRENT: Partial<Record<Sfx, number>> = {
  campfire: 1,
  player_death: 1,
  player_hurt: 2,
  level_up: 1,
  mob_aggro: 2,
  mob_death: 2,
  arena_death: 2,
  sword_swing: 2,
};

/** Per-cue gain trim, so a loud generated clip doesn't tower over the others. */
const TRIM: Partial<Record<Sfx, number>> = {
  footstep_grass: 0.35,
  footstep_soil: 0.35,
  ui_click: 0.4,
  ui_open: 0.5,
  ui_close: 0.5,
  chat_message: 0.45,
  campfire: 0.3,
  horse_gallop: 0.5,
  mob_aggro: 0.7,
  mob_death: 0.8,
  arena_death: 0.8,
  sword_swing: 0.8,
};

/**
 * Distance falloff for world sounds, in world pixels (a tile is 48).
 * Full volume within ~3 tiles, inaudible past ~16 — roughly the fog's vision.
 */
const HEAR_FULL_PX = 3 * 48;
const HEAR_MAX_PX = 16 * 48;

export interface AudioSettings {
  musicVolume: number;   // 0..1
  sfxVolume: number;     // 0..1
  muted: boolean;        // master mute — silences both, keeps the volumes
}

const STORE_KEY = 'terraboma:audio';
const DEFAULTS: AudioSettings = { musicVolume: 0.35, sfxVolume: 0.7, muted: false };

function loadSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const s = raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
    return { ...s, muted: !!s.muted };
  } catch { return { ...DEFAULTS }; }
}

let settings = loadSettings();
let unlocked = false;                    // has the player interacted yet?
let current: MusicTrack | null = null;
let bed: HTMLAudioElement | null = null;
let fade: number | null = null;
const pools = new Map<string, HTMLAudioElement[]>();
const cursors = new Map<string, number>();
/**
 * Variant files (`cue_2`, `cue_3`) we have CONFIRMED exist, and base cues we have
 * confirmed do not. Variants are opt-in on proof: an optimistic pick that 404s
 * plays nothing, so a variant is only ever offered once it has actually loaded.
 */
const readyVariants = new Set<string>();
const missingCues = new Set<string>();
/** Cues whose optional variants have already been probed (once each, ever). */
const probed = new Set<string>();
const gate = createCueGate({ cooldownMs: RETRIGGER_MS, maxConcurrent: MAX_CONCURRENT });
/** Hero position, for distance-attenuated world sounds. */
let listenerX = 0, listenerY = 0, hasListener = false;

export function getAudioSettings(): AudioSettings { return { ...settings }; }

/** Effective music gain for the bed that is playing (mute + per-track trim). */
function musicGain(track: MusicTrack | null): number {
  if (settings.muted || !track) return 0;
  return settings.musicVolume * TRACK_GAIN[track];
}

export function setAudioSettings(next: Partial<AudioSettings>): void {
  const wasMuted = settings.muted;
  settings = { ...settings, ...next };
  try { localStorage.setItem(STORE_KEY, JSON.stringify(settings)); } catch { /* private mode */ }
  if (bed) bed.volume = musicGain(current);
  // Un-muting mid-session must actually resume the bed: while muted we keep the
  // element playing at volume 0 rather than tearing it down, so there is nothing
  // to restart — but a bed that was never started (muted at boot) needs one.
  if (wasMuted && !settings.muted && !bed) applyScene();
  if (settings.muted) {
    for (const pool of pools.values()) for (const a of pool) { a.pause(); a.currentTime = 0; }
    gate.reset();
  }
}

/**
 * Arm audio on the first user gesture.
 *
 * Called once at boot; the listeners remove themselves. Any music requested before
 * this point is remembered and started here, so `playMusic('day')` during scene
 * setup does the right thing without the caller knowing about autoplay policy.
 */
const GESTURES = ['pointerdown', 'keydown', 'touchstart'] as const;

export function initAudio(): void {
  if (unlocked) return;
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    for (const ev of GESTURES) window.removeEventListener(ev, unlock);
    if (pending) { const t = pending; pending = null; playMusic(t); }
  };
  for (const ev of GESTURES) window.addEventListener(ev, unlock, { once: false, passive: true });
}

/**
 * Wait for the next gesture and try this bed again.
 *
 * `unlocked` only means "we have seen an interaction" — it is not a promise that
 * playback will be allowed. A synthetic event, or a gesture the browser does not
 * count as user activation, unlocks us while `play()` still rejects. Without this
 * retry the failed bed stayed recorded as the CURRENT track, so every later call
 * hit the already-playing guard and the game was silent for the rest of the
 * session. Now a rejection forgets the bed and re-arms on the next gesture.
 */
function armRetry(track: MusicTrack): void {
  pending = track;
  const retry = () => {
    for (const ev of GESTURES) window.removeEventListener(ev, retry);
    const t = pending; pending = null;
    if (t) playMusic(t);
  };
  for (const ev of GESTURES) window.addEventListener(ev, retry, { once: true, passive: true });
}

let pending: MusicTrack | null = null;

/**
 * Switch the music bed, cross-fading out of whatever is playing.
 *
 * `null` fades out and stops — used when leaving the farm for a view that should
 * be quiet. Prefer `setAudioView()`: this is the mechanism, that is the policy.
 */
export function playMusic(track: MusicTrack | null): void {
  if (track === current) return;
  // Deliberately does NOT set `current` here. It used to, and that was the bug that
  // made the game silent: on unlock we replay the pending track, but playMusic()
  // starts with an "already playing?" guard — with `current` pre-set to the very
  // track we were about to start, that guard returned immediately and the bed never
  // began. `current` is only ever set once a track is genuinely starting.
  if (!unlocked) { pending = track; return; }
  current = track;

  const old = bed;
  if (fade !== null) { clearInterval(fade); fade = null; }

  if (!track) {
    fadeOutAndStop(old);
    bed = null;
    return;
  }

  const next = new Audio(`${MUSIC_DIR}/${track}.m4a`);
  next.loop = true;
  next.volume = 0;
  next.preload = 'auto';
  next.addEventListener('error', () => { if (bed === next) { bed = null; current = null; } });
  bed = next;
  // Still blocked (or the file is missing)? Fail quietly, forget this bed, and try
  // again on the next gesture rather than pretending it is playing forever.
  void next.play().catch(() => {
    if (bed !== next) return;
    bed = null; current = null;
    if (fade !== null) { clearInterval(fade); fade = null; }
    armRetry(track);
  });

  // Cross-fade over ~1.2s: the new bed rises as the old one falls.
  const step = 0.05;
  const target = musicGain(track);
  fade = window.setInterval(() => {
    if (next.volume < target) next.volume = Math.min(target, next.volume + Math.max(0.01, target * step));
    if (old && old.volume > 0) old.volume = Math.max(0, old.volume - step);
    if (next.volume >= target - 0.001 && (!old || old.volume <= 0)) {
      if (old) { old.pause(); old.src = ''; }
      if (fade !== null) { clearInterval(fade); fade = null; }
    }
  }, 60);
}

function fadeOutAndStop(el: HTMLAudioElement | null): void {
  if (!el) return;
  const id = window.setInterval(() => {
    el.volume = Math.max(0, el.volume - 0.05);
    if (el.volume <= 0) { el.pause(); el.src = ''; clearInterval(id); }
  }, 60);
}

// ─── View → music ownership ──────────────────────────────────────────────────
// One scene object, one resolver. Views never pick a track themselves.

const scene: AudioScene = { view: 'farm', overlay: null, phase: 'day' };
/**
 * Has the scene ever been applied? The setters skip the work when nothing
 * changed, and at boot NOTHING has changed — the shell opens on the farm, which
 * is already the initial view, and the world clock may well already say day. That
 * left the very first mount with no bed at all until the player happened to cross
 * dusk. The first call always applies, whatever it says.
 */
let applied = false;

function applyScene(): void { applied = true; playMusic(resolveTrack(scene)); }

/** The shell switched tabs. */
export function setAudioView(view: AudioView): void {
  if (scene.view === view && applied) return;
  scene.view = view;
  applyScene();
}

/**
 * A fullscreen overlay took over (an arena match, a dungeon) — or handed control
 * back when passed `null`. Clearing it is what stops the arena bed and lets the
 * farm reclaim day/night the moment a match ends.
 */
export function setAudioOverlay(overlay: AudioView | null): void {
  if ((scene.overlay ?? null) === overlay && applied) return;
  scene.overlay = overlay;
  applyScene();
}

/** The world clock crossed into day or night (dusk/dawn count as night). */
export function setWorldPhase(phase: 'day' | 'night'): void {
  if (scene.phase === phase && applied) return;
  scene.phase = phase;
  applyScene();
}

/** Where the hero is, so world sounds can fade with distance. */
export function setListener(x: number, y: number): void {
  listenerX = x; listenerY = y; hasListener = true;
}

// ─── Sound effects ───────────────────────────────────────────────────────────

/** Fetch (or reuse) the pool for one concrete file name. */
function poolFor(name: string): HTMLAudioElement[] {
  let pool = pools.get(name);
  if (!pool) {
    pool = Array.from({ length: POOL }, () => {
      const a = new Audio(`${SFX_DIR}/${name}.mp3`);
      a.preload = 'auto';
      // A file that isn't there is not an error worth surfacing — remember it so a
      // cue with no recording yet (`arena_death`) can fall back to its stand-in.
      a.addEventListener('error', () => { missingCues.add(name); });
      return a;
    });
    pools.set(name, pool);
    cursors.set(name, 0);
  }
  return pool;
}

/**
 * Ask, once per cue, whether `cue_2` / `cue_3` exist — and remember only the ones
 * that answer yes. A probe is a bare `Audio` with `preload=metadata`: one cheap
 * request that either resolves (variant registered) or 404s (never asked again).
 */
function probeVariants(cue: string, maxVariant = 3): void {
  if (probed.has(cue)) return;
  probed.add(cue);
  for (let i = 2; i <= maxVariant; i++) {
    const name = `${cue}_${i}`;
    const probe = new Audio(`${SFX_DIR}/${name}.mp3`);
    probe.preload = 'metadata';
    probe.addEventListener('loadedmetadata', () => readyVariants.add(name), { once: true });
    probe.addEventListener('error', () => undefined, { once: true });
  }
}

export interface SfxOpts {
  /** Extra gain (0..1) on top of the cue trim — used for distance falloff. */
  gain?: number;
  /** Play this instead when `cue`'s file turns out not to exist. */
  fallback?: Sfx;
  /**
   * Per-CALL overrides of the cue's gating. The table defaults suit the cue in
   * general; a specific call site often knows better — a mob's death growl in a
   * twelve-strong night pack needs a far longer leash than the same cue fired once
   * in the arena. Whichever is stricter wins, so a call site can tighten but never
   * loosen a cue past its table limit.
   */
  cooldownMs?: number;
  maxConcurrent?: number;
}

/**
 * Fire a sound effect. Safe to call as often as you like — unknown cues, blocked
 * autoplay, missing files and floods of identical cues are all no-ops rather than
 * errors.
 */
export function playSfx(cue: Sfx, opts: SfxOpts = {}): void {
  if (!unlocked || settings.muted || settings.sfxVolume <= 0) return;
  const gain = opts.gain ?? 1;
  if (gain <= 0.01) return;                       // too far away to hear
  // A cue whose own base file is missing falls back to its stand-in (e.g. the
  // not-yet-generated arena death → the ordinary mob death).
  const base = opts.fallback && missingCues.has(cue) ? opts.fallback : cue;
  // Stricter of (table default, call-site override).
  const tableCooldown = CUE_COOLDOWN_MS[base];
  const tableCap = CUE_MAX_CONCURRENT[base];
  const cooldown = Math.max(tableCooldown ?? 0, opts.cooldownMs ?? 0) || undefined;
  const cap = tableCap !== undefined || opts.maxConcurrent !== undefined
    ? Math.min(tableCap ?? Infinity, opts.maxConcurrent ?? Infinity)
    : undefined;
  // The shared gate carries the common case; a cue with its own numbers is checked
  // against them first, then still recorded in the gate so the counters stay right.
  if (cooldown !== undefined || cap !== undefined) {
    const now = performance.now();
    if (cap !== undefined && gate.active(base) >= cap) return;
    if (cooldown !== undefined && !customCooldownOk(base, now, cooldown)) return;
  }
  if (!gate.tryPlay(base, performance.now())) return;

  probeVariants(base);
  const name = pickVariant(base, readyVariants);
  const pool = poolFor(name);
  const i = (cursors.get(name) ?? 0) % pool.length;
  cursors.set(name, i + 1);
  const el = pool[i];
  el.volume = Math.max(0, Math.min(1, settings.sfxVolume * (TRIM[base] ?? 1) * gain));
  try { el.currentTime = 0; } catch { /* not seekable yet */ }
  // Free the concurrency slot when the clip ends — or, if it never reports (a
  // missing file, a blocked play), after its nominal length so the cue can't
  // deadlock itself into permanent silence.
  let freed = false;
  const free = () => { if (freed) return; freed = true; gate.release(base); };
  el.addEventListener('ended', free, { once: true });
  window.setTimeout(free, 4_000);
  const p = el.play();
  if (p && typeof p.catch === 'function') p.catch(() => { free(); missingCheck(name, el); });
}

/** A file that refuses to play at all (decode/404): stop offering it. */
function missingCheck(name: string, el: HTMLAudioElement): void {
  if (el.error) { missingCues.add(name); readyVariants.delete(name); }
}

const customLast = new Map<string, number>();
function customCooldownOk(cue: string, now: number, cooldownMs: number): boolean {
  const prev = customLast.get(cue);
  if (prev !== undefined && now - prev < cooldownMs) return false;
  customLast.set(cue, now);
  return true;
}

/**
 * Fire a world sound at a position — quieter the further it is from the hero.
 *
 * This is what keeps a scrap on the far side of the map from sounding like it is
 * happening in the player's ear. Falls back to full volume until a listener
 * position has been reported.
 */
export function playSfxAt(cue: Sfx, x: number, y: number, opts: SfxOpts = {}): void {
  if (!hasListener) { playSfx(cue, opts); return; }
  const d = Math.hypot(x - listenerX, y - listenerY);
  const g = distanceGain(d, HEAR_FULL_PX, HEAR_MAX_PX);
  if (g <= 0) return;
  playSfx(cue, { ...opts, gain: (opts.gain ?? 1) * g });
}

/** Stop everything (view teardown, sign-out). */
export function stopAudio(): void {
  playMusic(null);
  for (const pool of pools.values()) for (const a of pool) { a.pause(); a.currentTime = 0; }
  gate.reset();
  customLast.clear();
  hasListener = false;
  // Sign-out: the next mount must re-apply its scene from nothing.
  applied = false;
}
